import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons/faCircleInfo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Flex, Text } from "@mantine/core";
import type { PropsWithChildren, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import { DefaultBottomBarButtons } from "@/components/screener-builder/common/default-bottom-bar-buttons";
import {
  TOTAL_SCREENER_BUILDER_TITLE_SECTION_HEIGHT_PX,
  ScreenerBuilderWrapper,
} from "@/components/screener-builder/common/wrapper";
import {
  GenerationStatus,
  ScreenerBuilderStep,
} from "@/types/screener-builder";
import { useTrackAnalytics } from "@/hooks/analytics.hooks";

interface ScreenerBuilderFormWrapperProps {
  bottomBarLeftSection?: ReactNode;
  furthestProgressedStep: ScreenerBuilderStep;
  generationStatus: GenerationStatus;
  helpText: string;
  hideBottomBar?: boolean;
  isLoading?: boolean;
  jobTitle?: string;
  onBack?: () => void;
  onSave: (progressStep: boolean) => void;
  pageTitle: string;
}

export const ScreenerBuilderFormWrapper = ({
  bottomBarLeftSection,
  children,
  furthestProgressedStep,
  generationStatus,
  helpText,
  hideBottomBar = false,
  isLoading = false,
  jobTitle,
  onBack,
  onSave,
  pageTitle,
}: PropsWithChildren<ScreenerBuilderFormWrapperProps>) => {
  const track = useTrackAnalytics();

  return (
    <ScreenerBuilderWrapper
      bottomBarButtons={
        <DefaultBottomBarButtons
          leftSection={bottomBarLeftSection}
          onBack={onBack}
          onNext={() => onSave(true)}
          onSave={() => onSave(false)}
        />
      }
      furthestProgressedStep={furthestProgressedStep}
      generationStatus={generationStatus}
      hideBottomBar={hideBottomBar}
      isLoading={isLoading}
      jobDisplayName={jobTitle}
      pageTitle={pageTitle}
    >
      <Flex className="w-full pb-3">
        {/* Form Wrapper */}
        <Flex
          className={twMerge([
            "bg-white",
            "border",
            "border-uw-gray-2",
            "h-min",
            "mr-3",
            "rounded",
            "px-[30px]",
            "py-6",
            "w-[460px]",
          ])}
        >
          {children}
        </Flex>

        {/* Info Box */}
        <Flex
          className={twMerge([
            "bg-[#F0EBFA]",
            "border",
            "border-[#E0D6F5]",
            "flex-col",
            "h-min",
            "max-w-[360px]",
            "px-[30px]",
            "py-6",
            "rounded",
            "sticky",
            "text-start",
          ])}
          style={{
            top: `${TOTAL_SCREENER_BUILDER_TITLE_SECTION_HEIGHT_PX}px`,
          }}
        >
          <Flex direction="row">
            <FontAwesomeIcon
              className={twMerge([
                "h-[18px]",
                "mr-3",
                "text-uw-purple",
                "w-[18px]",
              ])}
              icon={faCircleInfo}
            />
            <Text c="gray.9" className="font-semibold" size="sm">
              Why do we need this?
            </Text>
          </Flex>
          <Text
            c="gray.9"
            className="my-3 text-sm text-pretty whitespace-pre-wrap"
          >
            {helpText}
          </Text>
          <Text c="gray.9" className="text-sm">
            Need help? Email{" "}
            <a
              className={twMerge(["font-semibold", "text-uw-purple"])}
              onClick={() => track("Email Support Button Clicked")}
              href="mailto:support@upwage.com"
            >
              support@upwage.com
            </a>
            {"."}
          </Text>
        </Flex>
      </Flex>
    </ScreenerBuilderWrapper>
  );
};
